import { runDifficultySettings, successProbability } from "@roguelife/engine";
import type {
  ActionView,
  EventView,
  GrantedItemView,
  RunDifficultyLevel,
  RunMode,
  RunResultView,
  UserView,
} from "./types";

type MockRun = {
  view: EventView;
  selectedAction: ActionView | null;
  lastRollOutcome: number | null;
  rolls: number;
  step: number;
};

const runs = new Map<string, MockRun>();
const users = new Map<string, UserView>();
const now = () => Date.now();

const secondAction: ActionView = {
  id: "mock-follow-lantern",
  name: "沿灯火入驿",
  des: "趁风声暂歇，跟随纸灯投下的窄光穿过桥洞。",
  req: 0,
  needsCheck: false,
  currentPoints: 0,
  requiredPoints: 0,
  probability: 1,
  pointBreakdown: [],
};

const oathAction: ActionView = {
  id: "mock-answer-oath-light",
  name: "借雨誓碎片叩门",
  des: "让袖中的碎片贴近纸灯，听它把刚才守住的誓约重新说给门内的人听。",
  req: 0,
  needsCheck: true,
  currentPoints: 1,
  requiredPoints: 8,
  probability: 0.72,
  pointBreakdown: [
    {
      stat: "雨誓碎片",
      adjustment: 1,
      coefficient: 1,
      points: 1,
      sourceItemIds: ["mock-rain-oath-shard"],
      sourceLabels: ["雨誓碎片"],
      sourceContributions: [
        {
          sourceItemId: "mock-rain-oath-shard",
          sourceLabel: "雨誓碎片",
          adjustment: 1,
          points: 1,
        },
      ],
    },
  ],
};

const omenItem: GrantedItemView = {
  id: "mock-rain-oath-shard",
  name: "雨誓碎片",
  eff: "每当挑战通过时，下一次检定获得一点额外助力。",
  des: "一枚湿冷的石片，握紧时会映出断裂界碑的微光。",
  gained: true,
  imageUrl: null,
};

const missedOmenItem: GrantedItemView = {
  ...omenItem,
  gained: false,
};

export const mockApi = {
  async newRun(opts: {
    mode: RunMode;
    difficulty?: RunDifficultyLevel;
    userId?: string;
  }): Promise<{ runId: string; view: EventView }> {
    await delay(120);
    const runId = `mock-run-${now()}`;
    const view = makeView({
      runId,
      mode: opts.mode,
      difficulty: opts.difficulty ?? 2,
      step: 0,
      luck: startingLuck(opts.difficulty ?? 2),
    });
    runs.set(runId, { view, selectedAction: null, lastRollOutcome: null, rolls: 0, step: 0 });
    return { runId, view };
  },

  async upsertUser(opts: { id: string; name: string }): Promise<{ user: UserView }> {
    await delay(40);
    const existing = users.get(opts.id);
    const user = {
      id: opts.id,
      name: opts.name,
      created_at: existing?.created_at ?? now(),
      updated_at: now(),
    };
    users.set(opts.id, user);
    return { user };
  },

  async userResults(_id: string): Promise<{ results: RunResultView[] }> {
    await delay(40);
    return { results: [] };
  },

  async getRun(id: string): Promise<{ view: EventView }> {
    await delay(60);
    return { view: getRun(id).view };
  },

  async select(id: string, actionId: string, numDice: number): Promise<{ view: EventView }> {
    await delay(90);
    const run = getRun(id);
    const action = run.view.actions.find((it) => it.id === actionId);
    if (!action) throw new Error("mock action not found");
    run.selectedAction = action;
    run.view = {
      ...run.view,
      pending: action.needsCheck
        ? {
            actionId,
            needsCheck: true,
            currentPoints: action.currentPoints,
            requiredPoints: action.requiredPoints,
            probability: action.probability,
            numDice,
            pointBreakdown: action.pointBreakdown,
            rerollsTaken: 0,
            maxRerolls: run.view.difficulty.maxRerollsPerCheck,
          }
        : {
            actionId,
            needsCheck: false,
            currentPoints: 0,
            requiredPoints: 0,
            probability: 1,
            numDice,
            pointBreakdown: [],
          },
    };
    return { view: run.view };
  },

  async roll(id: string, numDice: number): Promise<{ view: EventView }> {
    await delay(260);
    const run = getRun(id);
    if (!run.view.pending) throw new Error("mock pending check not found");
    const isReroll = !!run.view.pending.rolled;
    const previousRerolls = run.view.pending.rerollsTaken ?? 0;
    const maxRerolls = run.view.difficulty.maxRerollsPerCheck;
    if (isReroll && run.view.pending.rolled?.outcome !== 1) {
      throw new Error("mock only normal failures can be rerolled");
    }
    if (isReroll && maxRerolls != null && previousRerolls >= maxRerolls) {
      throw new Error("mock reroll limit reached");
    }
    run.rolls += 1;
    const dice = scriptedDice(run.view.difficulty.level, run.rolls, numDice);
    const total = dice.reduce((sum, value) => sum + value, 0) + run.view.pending.currentPoints;
    const crit = dice.slice(0, 2).every((value) => value === 6)
      ? "success"
      : dice.slice(0, 2).every((value) => value === 1)
        ? "fail"
        : "none";
    const outcome = crit === "success" ? 3 : crit === "fail" ? 0 : total >= run.view.pending.requiredPoints ? 2 : 1;
    const rerollsTaken = previousRerolls + (isReroll ? 1 : 0);
    const nextLuck = Math.max(0, run.view.player.luck - (isReroll ? 1 : 0)) + (outcome === 3 ? 1 : 0);
    const canReroll =
      outcome === 1 &&
      nextLuck > 0 &&
      (maxRerolls == null || rerollsTaken < maxRerolls);
    run.lastRollOutcome = outcome;
    run.view = {
      ...run.view,
      pending: {
        ...run.view.pending,
        numDice,
        rolled: { dice, total, crit, outcome },
        canReroll,
        rerollCost: 1,
        rerollsTaken,
        maxRerolls,
        rerollBlockedReason:
          outcome === 1 && !canReroll
            ? maxRerolls != null && rerollsTaken >= maxRerolls
              ? "limit"
              : "luck"
            : undefined,
      },
      player: {
        ...run.view.player,
        luck: nextLuck,
      },
    };
    return { view: run.view };
  },

  async continue(id: string): Promise<{ view: EventView }> {
    await delay(120);
    const run = getRun(id);
    run.step += 1;
    run.view = makeView({
      runId: id,
      mode: run.view.mode,
      difficulty: run.view.difficulty.level,
      step: run.step,
      luck: run.view.player.luck,
      lastRollOutcome: run.lastRollOutcome,
    });
    run.selectedAction = null;
    return { view: run.view };
  },
};

function makeView({
  runId,
  mode,
  difficulty,
  step,
  luck = 3,
  lastRollOutcome = null,
}: {
  runId: string;
  mode: RunMode;
  difficulty: RunDifficultyLevel;
  step: number;
  luck?: number;
  lastRollOutcome?: number | null;
}): EventView {
  const atSecondScene = step > 0;
  const settings = runDifficultySettings(difficulty);
  const action = firstActionForDifficulty(difficulty);
  const challengePassed = lastRollOutcome == null || lastRollOutcome >= 2;
  return {
    runId,
    mode,
    status: "active",
    difficulty: {
      level: settings.level,
      label: settings.label,
      scoreMultiplier: settings.scoreMultiplier,
      maxRerollsPerCheck: settings.maxRerollsPerCheck,
      rules: settings.rules,
    },
    event: atSecondScene
      ? {
          id: "mock-rain-inn",
          name: "夜雨驿站",
          des: challengePassed
            ? "纸灯被雨线压得很低，门内的炉火忽明忽暗。界碑碎片在袖中一热，像在提醒你：刚才的选择已经改变了这条路。"
            : "纸灯被雨线压得很低，门缝却渗出冰冷的黑风。界碑裂光没有跟上你的脚步，驿站里每一盏灯都像在迟疑。",
          imageUrl: null,
        }
      : {
          id: "mock-broken-beacon",
          name: "断裂界碑",
          des: "黑雨落在半截石碑上，裂缝里浮着微弱蓝火。远处驿站只剩一盏纸灯，而碑下有新鲜脚印正被雨水抹去。",
          imageUrl: null,
        },
    grantedItems: atSecondScene ? [challengePassed ? omenItem : missedOmenItem] : [],
    challenges: atSecondScene
      ? [
          {
            name: "雨中守誓",
            requirements: "刚才的检定达到命数门槛",
            passed: challengePassed,
            info: challengePassed
              ? "界碑没有完全闭合，但它承认了你，驿站的门因此开了一线。"
              : "裂光在掌心碎开，驿站的门仍然开启，却把更冷的风一并放了进来。",
          },
        ]
      : [],
    actions: atSecondScene ? [challengePassed ? oathAction : secondAction] : [action],
    player: {
      luck,
      adjustments: challengePassed && atSecondScene
        ? { 界碑残纹: 2, 夜路决心: 1, 雨誓碎片: 1 }
        : { 界碑残纹: 2, 夜路决心: 1 },
      flags: atSecondScene
        ? challengePassed
          ? { rain_oath_kept: 1 }
          : { rain_oath_fractured: 1 }
        : {},
      items: atSecondScene && challengePassed
        ? [{ id: omenItem.id, name: omenItem.name, eff: omenItem.eff, imageUrl: null }]
        : [],
    },
    pending: null,
    result: null,
  };
}

function startingLuck(difficulty: RunDifficultyLevel): number {
  return Math.max(0, 2 + runDifficultySettings(difficulty).startingLuckDelta);
}

function firstActionForDifficulty(difficulty: RunDifficultyLevel): ActionView {
  const hard = difficulty >= 4;
  const brutal = difficulty >= 6;
  const extreme = difficulty >= 7;
  const easy = difficulty <= 1;
  const currentPoints = easy ? 7 : extreme ? 4 : hard ? 5 : 6;
  const requiredPoints = easy ? 13 : extreme ? 17 : brutal ? 16 : hard ? 15 : 14;
  return {
    id: "mock-stand-by-beacon",
    name: "扶住断裂界碑",
    des: easy
      ? "把掌心按进雨水与裂光之间，旧誓立刻像认出归人一样亮起。"
      : hard
        ? "把掌心按进雨水与裂光之间，裂缝深处传来抵触的低鸣。"
        : "把掌心按进雨水与裂光之间，让旧誓重新认出你的名字。",
    req: 0,
    needsCheck: true,
    currentPoints,
    requiredPoints,
    probability: successProbability(currentPoints, requiredPoints, 2),
    pointBreakdown: [
      { stat: "界碑残纹", adjustment: hard ? 1 : 2, coefficient: 2, points: hard ? 2 : 4 },
      { stat: "夜路决心", adjustment: 1, coefficient: easy ? 3 : 2, points: easy ? 3 : 2 },
    ],
  };
}

function scriptedDice(difficulty: RunDifficultyLevel, rollAttempt: number, numDice: number): number[] {
  const extra = Array.from({ length: Math.max(0, numDice - 2) }, () => (difficulty >= 4 ? 2 : 4));
  if (rollAttempt > 1) return [6, 5, ...extra];
  if (difficulty <= 1) return [6, 6, ...extra];
  if (difficulty >= 7) return [2, 1, ...extra];
  if (difficulty >= 6) return [3, 2, ...extra];
  if (difficulty >= 4) return [2, 2, ...extra];
  return [6, 5, ...extra];
}

function getRun(id: string): MockRun {
  const run = runs.get(id);
  if (!run) throw new Error("mock run not found");
  return run;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
